import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, RefreshCw, Filter, Wifi, WifiOff } from 'lucide-react';
import { WaitlistDetailResponse } from '../types';
import { waitlistApi } from '../services/api';
import WaitlistCard from './WaitlistCard';
import WaitlistCardSkeleton from './WaitlistCardSkeleton';
import SlotOfferModal from './SlotOfferModal';
import { useWaitlistEvents, WaitlistEvent } from '../hooks/useWaitlistEvents';
import { useWaitlistAnalytics } from '../hooks/useAnalytics';
import { extractApiError } from '../utils/errorUtils';

/**
 * VendorWaitlistPage - Page showing all waitlisted fairs for current vendor
 * Displays all waitlist entries with statuses and allows managing offers
 * Includes real-time SSE updates (Issue #497)
 */
function VendorWaitlistPage() {
  const [entries, setEntries] = useState<WaitlistDetailResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [selectedOffer, setSelectedOffer] = useState<WaitlistDetailResponse | null>(null);

  // Analytics tracking (Issue #498)
  const { trackWaitlistPageViewed } = useWaitlistAnalytics();

  const loadWaitlist = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const data = await waitlistApi.getMyWaitlist();
      setEntries(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadWaitlist();
  }, []);

  // Track page view with current waitlist counts (Issue #498)
  useEffect(() => {
    if (!isLoading && entries.length >= 0) {
      const activeWaitlists = entries.filter(e => e.status === 'waiting').length;
      const pendingOffers = entries.filter(e => e.status === 'offered').length;

      trackWaitlistPageViewed({
        active_waitlists_count: activeWaitlists,
        pending_offers_count: pendingOffers,
      });
    }
  }, [isLoading, entries, trackWaitlistPageViewed]);

  // Real-time SSE event handlers (Issue #497)
  const handlePositionChanged = useCallback((event: WaitlistEvent) => {
    console.log('Position changed event received:', event);
    // Refresh waitlist data to show updated position
    loadWaitlist(true);
  }, []);

  const handleSlotOffered = useCallback((event: WaitlistEvent) => {
    console.log('Slot offered event received:', event);
    // Refresh waitlist data to show new offer
    loadWaitlist(true);
  }, []);

  const handleOfferExpiring = useCallback((event: WaitlistEvent) => {
    console.log('Offer expiring event received:', event);
    // Refresh to show updated expiration status
    loadWaitlist(true);
  }, []);

  const handleOfferExpired = useCallback((event: WaitlistEvent) => {
    console.log('Offer expired event received:', event);
    // Refresh to remove expired offer
    loadWaitlist(true);
  }, []);

  const handleRemovedFromWaitlist = useCallback((event: WaitlistEvent) => {
    console.log('Removed from waitlist event received:', event);
    // Refresh to remove entry
    loadWaitlist(true);
  }, []);

  // Connect to SSE for real-time updates (Issue #497)
  const { connectionStatus, reconnect } = useWaitlistEvents(true, {
    onPositionChanged: handlePositionChanged,
    onSlotOffered: handleSlotOffered,
    onOfferExpiring: handleOfferExpiring,
    onOfferExpired: handleOfferExpired,
    onRemovedFromWaitlist: handleRemovedFromWaitlist,
  });

  const handleRefresh = () => {
    loadWaitlist(true);
  };

  const handleEntryRemoved = () => {
    loadWaitlist(true);
  };

  const handleOfferAction = (entry: WaitlistDetailResponse) => {
    setSelectedOffer(entry);
  };

  const handleOfferAccept = () => {
    setSelectedOffer(null);
    loadWaitlist(true);
  };

  const handleOfferDecline = () => {
    setSelectedOffer(null);
    loadWaitlist(true);
  };

  const filteredEntries = entries.filter(entry => {
    if (statusFilter === 'active') {
      return entry.status === 'waiting' || entry.status === 'notified';
    }
    if (statusFilter === 'history') {
      return entry.status === 'converted' || entry.status === 'expired' || entry.status === 'cancelled';
    }
    return true;
  });

  if (isLoading && entries.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Waitlists</h1>
            <p className="text-gray-600">Track your waitlist positions and slot offers</p>
          </div>
        </div>
        <div className="space-y-4">
          <WaitlistCardSkeleton />
          <WaitlistCardSkeleton />
          <WaitlistCardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Waitlists</h1>
          <p className="text-gray-600">Track your waitlist positions and slot offers</p>
          {/* SSE Connection Status Indicator (Issue #497) */}
          <div className="flex items-center gap-2 mt-2">
            {connectionStatus === 'connected' && (
              <>
                <Wifi className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-600">Live updates active</span>
              </>
            )}
            {connectionStatus === 'connecting' && (
              <>
                <Wifi className="w-4 h-4 text-yellow-600 animate-pulse" />
                <span className="text-xs text-yellow-600">Connecting...</span>
              </>
            )}
            {(connectionStatus === 'disconnected' || connectionStatus === 'error') && (
              <>
                <WifiOff className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">Disconnected</span>
                <button
                  onClick={reconnect}
                  className="text-xs text-secondary hover:text-primary-dark underline"
                >
                  Reconnect
                </button>
              </>
            )}
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Show:</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-3 py-1 text-sm rounded-md ${
                statusFilter === 'active'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active ({entries.filter(e => e.status === 'waiting' || e.status === 'notified').length})
            </button>
            <button
              onClick={() => setStatusFilter('history')}
              className={`px-3 py-1 text-sm rounded-md ${
                statusFilter === 'history'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              History ({entries.filter(e => e.status === 'converted' || e.status === 'expired' || e.status === 'cancelled').length})
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 text-sm rounded-md ${
                statusFilter === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({entries.length})
            </button>
          </div>
        </div>
      </div>

      {/* Waitlist Cards */}
      <div className="space-y-4">
        {filteredEntries.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {statusFilter === 'active' ? 'No Active Waitlists' : 'No Entries Found'}
            </h3>
            <p className="text-gray-600 mb-4">
              {statusFilter === 'active'
                ? "You're not currently on any waitlists."
                : `No ${statusFilter} waitlist entries.`}
            </p>
            <p className="text-sm text-gray-500">
              Join a waitlist when you find a fair with no available slots
            </p>
          </div>
        ) : (
          <>
            {filteredEntries.map((entry) => (
              <WaitlistCard
                key={entry.id}
                entry={entry}
                onRemoved={handleEntryRemoved}
                onOfferAction={() => handleOfferAction(entry)}
              />
            ))}
          </>
        )}
      </div>

      {/* Slot Offer Modal */}
      {selectedOffer && (
        <SlotOfferModal
          entry={selectedOffer}
          isOpen={!!selectedOffer}
          onClose={() => setSelectedOffer(null)}
          onAccept={handleOfferAccept}
          onDecline={handleOfferDecline}
        />
      )}
    </div>
  );
}

export default VendorWaitlistPage;
